import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Folder,
  Upload,
  Download, 
  Eye,
  Trash2,
  Plus,
  FileText,
  Image,
  FileIcon,
  X,
  Calendar,
  User,
  Camera
} from 'lucide-react';
import { apiService } from '../../services/api';

export const ArquivosMoto = ({ moto, isOpen, onClose, onArquivoUpdated }) => {
  const [arquivos, setArquivos] = useState([]);
  const [pastas, setPastas] = useState([]);
  const [pastaAtual, setPastaAtual] = useState('geral');
  const [uploading, setUploading] = useState(false);
  const [novaPasta, setNovaPasta] = useState('');
  const [showNovaPasta, setShowNovaPasta] = useState(false);

  const pastasPadrao = [
    { nome: 'geral', label: 'Documentos Gerais', cor: 'bg-blue-100 text-blue-800' },
    { nome: 'fotos', label: 'Fotos da Moto', cor: 'bg-green-100 text-green-800' },
    { nome: 'documentos', label: 'Documentos Oficiais', cor: 'bg-purple-100 text-purple-800' },
    { nome: 'manutencao', label: 'Histórico Manutenção', cor: 'bg-orange-100 text-orange-800' },
    { nome: 'sinistros', label: 'Sinistros/Ocorrências', cor: 'bg-red-100 text-red-800' }
  ];

  useEffect(() => {
    if (isOpen && moto) {
      carregarArquivos();
      carregarPastas();
    }
  }, [isOpen, moto, pastaAtual]);

  const carregarArquivos = async () => {
    try {
      // Carregar arquivos reais da API
      const arquivosReais = await apiService.getArquivosMoto(moto.id);
      setArquivos(arquivosReais);
    } catch (error) {
      console.error('Erro ao carregar arquivos da moto:', error);
      // Se falhar na API, não mostrar arquivos
      setArquivos([]);
      console.error('Erro ao carregar arquivos da API da moto');
    }
  };

  const carregarPastas = async () => {
    try {
      // Combinar pastas padrão com pastas personalizadas
      setPastas(pastasPadrao);
    } catch (error) {
      console.error('Erro ao carregar pastas:', error);
    }
  };

  const handleUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (let file of files) {
        console.log(`Uploading ${file.name} para pasta ${pastaAtual}`);
        
        // Upload real usando a API
        await apiService.uploadArquivoMoto(moto.id, file, pastaAtual, `Arquivo da pasta ${pastaAtual}`);
      }
      
      alert(`${files.length} arquivo(s) enviado(s) com sucesso!`);
      carregarArquivos();
      
      // Notificar o componente pai sobre a atualização
      if (onArquivoUpdated) {
        onArquivoUpdated();
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      alert('Erro ao enviar arquivo(s). Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const criarNovaPasta = async () => {
    if (!novaPasta.trim()) return;
    
    try {
      const novaPastaObj = {
        nome: novaPasta.toLowerCase().replace(/\s+/g, '_'),
        label: novaPasta,
        cor: 'bg-gray-100 text-gray-800'
      };
      
      setPastas([...pastas, novaPastaObj]);
      setNovaPasta('');
      setShowNovaPasta(false);
      alert('Pasta criada com sucesso!');
    } catch (error) {
      console.error('Erro ao criar pasta:', error);
    }
  };

  const excluirArquivo = async (arquivo) => {
    if (window.confirm(`Tem certeza que deseja excluir "${arquivo.nome_original}"?`)) {
      try {
        // Exclusão real usando a API
        await apiService.deleteArquivo(arquivo.id);
        
        // Recarregar a lista de arquivos após exclusão
        await carregarArquivos();
        
        // Notificar o componente pai sobre a atualização
        if (onArquivoUpdated) {
          onArquivoUpdated();
        }
        
        alert('Arquivo excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir arquivo:', error);
        alert('Erro ao excluir arquivo. Tente novamente.');
      }
    }
  };

  const downloadArquivo = async (arquivo) => {
    try {
      console.log(`Downloading ${arquivo.nome_original}`);
      
      // Fazer download real usando a API
      const response = await apiService.downloadArquivo(arquivo.id);
      
      // Criar blob e fazer download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = arquivo.nome_original || 'arquivo';
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      console.log(`✅ Download de "${arquivo.nome_original}" concluído!`);
    } catch (error) {
      console.error('Erro no download:', error);
      alert(`Erro ao fazer download de "${arquivo.nome_original}". Tente novamente.`);
    }
  };
  const definirComoPerfilFoto = async (arquivo) => {
    try {
      // Primeiro, remover o perfil de todos os outros arquivos da moto
      await Promise.all(
        arquivos
          .filter(a => a.is_profile)
          .map(a => apiService.atualizarArquivoMoto(a.id, { is_profile: false }))
      );
      
      // Definir este arquivo como perfil
      await apiService.atualizarArquivoMoto(arquivo.id, { is_profile: true });
      
      // Recarregar arquivos
      await carregarArquivos();
      
      // Notificar o componente pai sobre a atualização
      if (onArquivoUpdated) {
        onArquivoUpdated();
      }
      
      alert('Foto definida como perfil da moto!');
    } catch (error) {
      console.error('Erro ao definir foto como perfil:', error);
      alert('Erro ao definir foto como perfil. Tente novamente.');
    }
  };

  const getIconeArquivo = (tipo) => {
    switch (tipo) {
      case 'image':
        return <Image className="h-8 w-8 text-green-500" />;
      case 'pdf':
        return <FileText className="h-8 w-8 text-red-500" />;
      default:
        return <FileIcon className="h-8 w-8 text-gray-500" />;
    }
  };

  const getIconeArquivoMelhorado = (arquivo) => {
    const isImage = arquivo.content_type?.startsWith('image/') || arquivo.nome_original?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/);
    const isPdf = arquivo.content_type === 'application/pdf' || arquivo.nome_original?.toLowerCase().endsWith('.pdf');
    const isDoc = arquivo.nome_original?.toLowerCase().match(/\.(doc|docx)$/);
    const isXls = arquivo.nome_original?.toLowerCase().match(/\.(xls|xlsx)$/);
    const isTxt = arquivo.content_type === 'text/plain' || arquivo.nome_original?.toLowerCase().endsWith('.txt');
    
    if (isImage) {
      return <Image className="h-7 w-7 text-blue-500" />;
    } else if (isPdf) {
      return <FileText className="h-7 w-7 text-red-500" />;
    } else if (isDoc) {
      return <FileText className="h-7 w-7 text-blue-600" />;
    } else if (isXls) {
      return <FileText className="h-7 w-7 text-green-600" />;
    } else if (isTxt) {
      return <FileText className="h-7 w-7 text-gray-600" />;
    } else {
      return <FileIcon className="h-7 w-7 text-gray-500" />;
    }
  };

  const formatarTamanho = (tamanho) => {
    if (!tamanho) return 'N/A';
    if (tamanho < 1024) return `${tamanho} B`;
    if (tamanho < 1024 * 1024) return `${(tamanho / 1024).toFixed(1)} KB`;
    return `${(tamanho / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'N/A';
    try {
      const data = new Date(dataString);
      return data.toLocaleDateString('pt-BR') + ' ' + data.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Data inválida';
    }
  };

  const visualizarArquivo = (arquivo) => {
    // Abrir em nova aba para visualizar
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/arquivos/${arquivo.id}`;
    window.open(url, '_blank');
  };

  const getPastaAtual = () => {
    return pastas.find(p => p.nome === pastaAtual) || pastas[0];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-slate-800/90 rounded-lg shadow-xl w-full max-w-6xl h-full md:h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Arquivos da Moto {moto?.placa}
            </h2>
            <p className="text-sm text-gray-600">
              {moto?.modelo} {moto?.cor} - {moto?.ano}
            </p>
          </div>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Sidebar com Pastas */}
          <div className="w-full md:w-64 bg-gray-50 p-4 overflow-y-auto border-b md:border-b-0 md:border-r max-h-48 md:max-h-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900">Pastas</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowNovaPasta(true)}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>

            {/* Lista de Pastas */}
            <div className="space-y-2">
              {pastas.map((pasta) => (
                <button
                  key={pasta.nome}
                  onClick={() => setPastaAtual(pasta.nome)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    pastaAtual === pasta.nome
                      ? 'bg-blue-100 border-blue-200 border'
                      : 'hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Folder className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-medium truncate">
                      {pasta.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Criar Nova Pasta */}
            {showNovaPasta && (
              <div className="mt-4 p-3 bg-white rounded border">
                <Label className="text-xs text-gray-600">Nome da Pasta:</Label>
                <Input
                  value={novaPasta}
                  onChange={(e) => setNovaPasta(e.target.value)}
                  placeholder="Ex: Revisões 2024"
                  className="mt-1 text-sm"
                />
                <div className="flex space-x-2 mt-2">
                  <Button size="sm" onClick={criarNovaPasta}>
                    Criar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowNovaPasta(false);
                      setNovaPasta('');
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Área Principal */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Toolbar da Pasta Atual */}
            <div className="p-3 md:p-4 border-b bg-white">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-3 md:space-y-0">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <Folder className="h-5 w-5 text-gray-600" />
                  <h3 className="font-semibold text-lg">
                    {getPastaAtual()?.label}
                  </h3>
                  <Badge className={getPastaAtual()?.cor}>
                    {arquivos.length} arquivo(s)
                  </Badge>
                </div>

                {/* Upload */}
                <div className="flex space-x-2">
                  <input
                    type="file"
                    multiple
                    onChange={handleUpload}
                    className="hidden"
                    id="upload-arquivos"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                  <Button
                    onClick={() => document.getElementById('upload-arquivos').click()}
                    disabled={uploading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'Enviando...' : 'Upload'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Lista de Arquivos */}
            <div className="flex-1 p-4 overflow-y-auto">
              {arquivos.length === 0 ? (
                <div className="text-center py-12">
                  <Folder className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">
                    Nenhum arquivo nesta pasta
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Clique em &ldquo;Upload&rdquo; para adicionar arquivos
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {arquivos.map((arquivo) => (
                    <div key={arquivo.id} className="bg-slate-800/90 border border-gray-200 rounded-lg p-3 md:p-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                        {/* Informações do Arquivo */}
                        <div className="flex items-start md:items-center space-x-3 md:space-x-4 flex-1 min-w-0">
                          {/* Ícone do Tipo de Arquivo */}
                          <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                            {getIconeArquivoMelhorado(arquivo)}
                          </div>
                          
                          {/* Detalhes do Arquivo */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col md:flex-row md:items-center space-y-1 md:space-y-0 md:space-x-2">
                              <h4 className="font-semibold text-gray-900 text-sm md:text-base truncate">
                                {arquivo.nome_original || arquivo.nome}
                              </h4>
                              {arquivo.is_profile && (
                                <Badge className="bg-blue-100 text-blue-800 text-xs w-fit">
                                  <Camera className="h-3 w-3 mr-1" />
                                  Foto de Perfil
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex flex-col md:flex-row md:items-center space-y-1 md:space-y-0 md:space-x-4 mt-1">
                              <span className="text-xs md:text-sm text-gray-500">
                                Tipo: <span className="font-medium">{arquivo.tipo}</span>
                              </span>
                              <span className="text-xs md:text-sm text-gray-500">
                                Tamanho: <span className="font-medium">{formatarTamanho(arquivo.tamanho)}</span>
                              </span>
                              <span className="text-xs md:text-sm text-gray-500">
                                <Calendar className="h-3 w-3 inline mr-1" />
                                {formatarData(arquivo.data_upload)}
                              </span>
                            </div>
                            
                            {arquivo.descricao && (
                              <p className="text-xs md:text-sm text-gray-600 mt-1 italic truncate">
                                &ldquo;{arquivo.descricao}&rdquo;
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Ações do Arquivo */}
                        <div className="flex items-center space-x-2 md:ml-4 justify-end md:justify-start">
                          {/* Preview para imagens */}
                          {arquivo.content_type?.startsWith('image/') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => visualizarArquivo(arquivo)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadArquivo(arquivo)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          
                          {/* Botão "Definir como Perfil" apenas para imagens */}
                          {arquivo.content_type?.startsWith('image/') && (
                            <Button
                              size="sm"
                              variant={arquivo.is_profile ? "default" : "outline"}
                              onClick={() => definirComoPerfilFoto(arquivo)}
                              className={arquivo.is_profile ? 'bg-blue-600 text-white' : 'text-blue-600 hover:text-blue-700'}
                              disabled={arquivo.is_profile}
                            >
                              <Camera className="h-4 w-4" />
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => excluirArquivo(arquivo)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};